import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Box, IconButton, MenuItem, Select, TextField } from '@mui/material'
import { ui, tone } from '../../../utils/uiTokens'
import { CRM_POLL, CrmIntakeData, crmApi } from '../../../features/crm/crmApi'
import {
    Card, CardHead, Chip, Chips, Empty, ErrorText, FieldLabel, Hint, Muted, OutlineButton, PageHead, Pill, PrimaryButton, TONES, contentSx, pageSx,
} from '../shared/crmUi'
import {
    GRADES, LANGS, QUOTAS, dictLabel, digits, formatPhone, hm, iamUserId, money, subjectTitle, todayKey,
} from '../shared/format'
import { useCrmDict } from '../shared/dict'
import { useAppSelector } from '../../../app/hooks'
import { sellerStage } from '../shared/stages'

interface Form {
    studentName: string
    studentPhone: string
    iin: string
    grade: string
    language: string
    parentName: string
    parentPhone: string
    packageCode: string
    subjectIds: number[]
    payType: string
    payments: string[]
    nextPaySum: string
    paidSum: string
    discountSum: string
    contractUrl: string
    quota: string
    sources: string[]
    amoUrl: string
    bookOrderNo: string
    bookSum: string
    note: string
    // РОП: чей ученик, 0 = РОП өзі
    sellerId: number | ''
}

const EMPTY: Form = {
    studentName: '',
    studentPhone: '',
    iin: '',
    grade: '',
    language: '',
    parentName: '',
    parentPhone: '',
    packageCode: '',
    subjectIds: [],
    payType: '',
    payments: [],
    nextPaySum: '',
    paidSum: '',
    discountSum: '',
    contractUrl: '',
    quota: '',
    sources: [],
    amoUrl: '',
    bookOrderNo: '',
    bookSum: '',
    note: '',
    sellerId: '',
}

const AMO_RE = /^https?:\/\/\S*(amocrm|kommo)\./i
const HTTP_RE = /^https?:\/\//i

const toggle = <T,>(list: T[], v: T) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v])

// rop - сату бөлімінің басшысы: + поле «Менеджер», без «Бүгін тіркелгендер», после сохранения - в «Барлық оқушылар»
const RegisterPage: React.FC<{ rop?: boolean }> = ({ rop }) => {
    const { formatMessage, locale } = useIntl()
    const dict = useCrmDict()
    const navigate = useNavigate()
    const [params, setParams] = useSearchParams()
    const editId = Number(params.get('edit')) || 0

    const [form, setForm] = useState<Form>(EMPTY)
    const [errors, setErrors] = useState<Partial<Record<keyof Form, string>>>({})
    const [dup, setDup] = useState('')
    const [saved, setSaved] = useState('')

    const { data: subjects = [] } = crmApi.useGetCrmSubjectsQuery()
    const { data: editing } = crmApi.useGetIntakeQuery(editId, { skip: !editId })
    const { data: today = [] } = crmApi.useListIntakesQuery({ tab: 'MY', from: todayKey(), to: todayKey() }, { pollingInterval: CRM_POLL, skip: rop })
    const { data: managers = [] } = crmApi.useGetManagersQuery({}, { skip: !rop })
    const [checkDuplicate] = crmApi.useLazyCheckDuplicateQuery()
    const [createIntake, { isLoading: creating }] = crmApi.useCreateIntakeMutation()
    const [updateIntake, { isLoading: updating }] = crmApi.useUpdateIntakeMutation()

    useEffect(() => {
        setErrors({})
        setDup('')
        if (!editId) {
            setForm(EMPTY)
            return
        }
        if (!editing || editing.id !== editId) return
        const d = editing.data
        setForm({
            studentName: d.studentName || '',
            studentPhone: formatPhone(d.studentPhone),
            iin: d.iin || '',
            grade: d.grade || '',
            language: d.language || '',
            parentName: d.parentName || '',
            parentPhone: formatPhone(d.parentPhone),
            packageCode: d.packageCode || '',
            subjectIds: d.subjectIds ?? editing.subjects?.map((s) => s.id) ?? [],
            payType: d.payType || '',
            payments: d.payments ?? [],
            nextPaySum: d.nextPaySum ? String(d.nextPaySum) : editing.nextPaySum ? String(editing.nextPaySum) : '',
            paidSum: d.paidSum ? String(d.paidSum) : '',
            discountSum: d.discountSum ? String(d.discountSum) : '',
            contractUrl: d.contractUrl || '',
            quota: d.quota || '',
            sources: d.sources ?? [],
            amoUrl: d.amoUrl || '',
            bookOrderNo: d.bookOrderNo || '',
            bookSum: d.bookSum ? String(d.bookSum) : '',
            note: d.note || '',
            sellerId: editing.sellerId,
        })
    }, [editId, editing])

    const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }))
    const installment = dict.isInstallment(form.payType)
    // ученик самого РОП (id не из списка продажников) - «РОП өзі» = 0
    const sellerValue = form.sellerId === '' || managers.some((m) => m.userId === form.sellerId) ? form.sellerId : 0
    const token = useAppSelector((st) => st.user.userInfo.token)
    // создание: 0 = РОП сам; изменение: 0 = оставить как есть, «РОП өзі» - его собственный iam id
    const ropSellerId = () => {
        const v = Number(sellerValue) || 0
        if (!editId) return v
        const cur = managers.some((m) => m.userId === editing?.sellerId) ? editing?.sellerId || 0 : 0
        if (v === cur) return 0
        return v || iamUserId(token)
    }

    const mainSubjects = subjects.filter((s) => s.kind === 'MAIN')
    const profileSubjects = subjects.filter((s) => s.kind === 'PROFILE')

    const runDuplicate = async (phone: string, iin: string) => {
        const p = digits(phone)
        const i = digits(iin)
        if (p.length !== 11 && i.length !== 12) return setDup('')
        try {
            const hit = await checkDuplicate({ phone: p.length === 11 ? p : undefined, iin: i.length === 12 ? i : undefined, excludeId: editId || undefined }).unwrap()
            setDup(hit ? formatMessage({ id: hit.matchedBy === 'IIN' ? 'crmDupIin' : 'crmDupPhone' }, { name: hit.studentName }) : '')
        } catch {
            setDup('')
        }
    }

    const validate = () => {
        const e: Partial<Record<keyof Form, string>> = {}
        const required = formatMessage({ id: 'crmRequired' })
        ;(['studentName', 'studentPhone', 'iin', 'grade', 'language', 'parentName', 'parentPhone', 'amoUrl', 'paidSum'] as const).forEach((k) => {
            if (!String(form[k]).trim()) e[k] = required
        })
        ;(['studentPhone', 'parentPhone'] as const).forEach((k) => {
            if (form[k].trim() && digits(form[k]).length !== 11) e[k] = formatMessage({ id: 'crmPhone11' })
        })
        if (form.iin.trim() && digits(form.iin).length !== 12) e.iin = formatMessage({ id: 'crmIin12' })
        if (form.amoUrl.trim() && !AMO_RE.test(form.amoUrl.trim())) e.amoUrl = formatMessage({ id: 'crmAmoInvalid' })
        if (form.contractUrl.trim() && !HTTP_RE.test(form.contractUrl.trim())) e.contractUrl = formatMessage({ id: 'crmUrlInvalid' })
        if (!form.subjectIds.length) e.subjectIds = formatMessage({ id: 'crmPickSubject' })
        if (!form.packageCode) e.packageCode = formatMessage({ id: 'crmPickPackage' })
        if (!form.payments.length) e.payments = formatMessage({ id: 'crmPickPayment' })
        if (!form.payType) e.payType = formatMessage({ id: 'crmPickPayType' })
        if (installment && !Number(form.nextPaySum)) e.nextPaySum = formatMessage({ id: 'crmNextPayRequired' })
        if (rop && form.sellerId === '') e.sellerId = formatMessage({ id: 'crmPickManager' })
        if (!form.sources.length) e.sources = formatMessage({ id: 'crmPickSource' })
        setErrors(e)
        return Object.keys(e).length === 0
    }

    const submit = async (ev: React.FormEvent) => {
        ev.preventDefault()
        setSaved('')
        if (!validate()) return
        const data: CrmIntakeData = {
            studentName: form.studentName.trim(),
            studentPhone: digits(form.studentPhone),
            iin: digits(form.iin),
            grade: form.grade,
            language: form.language,
            parentName: form.parentName.trim(),
            parentPhone: digits(form.parentPhone),
            packageCode: form.packageCode,
            subjectIds: form.subjectIds,
            payType: form.payType,
            payments: form.payments,
            nextPaySum: installment ? Number(form.nextPaySum) || 0 : 0,
            paidSum: Number(form.paidSum) || 0,
            discountSum: Number(form.discountSum) || 0,
            contractUrl: form.contractUrl.trim(),
            quota: form.quota,
            sources: form.sources,
            amoUrl: form.amoUrl.trim(),
            bookOrderNo: form.bookOrderNo.trim(),
            bookSum: Number(form.bookSum) || 0,
            note: form.note.trim(),
            sellerId: rop ? ropSellerId() : 0,
        }
        try {
            if (editId) {
                await updateIntake({ id: editId, data }).unwrap()
                toast.success(
                    formatMessage({ id: editing?.status === 'APPROVED' ? 'crmEditSavedNotified' : 'crmEditSaved' }, { name: data.studentName }),
                )
                navigate(rop ? `/rop/students/${editId}` : '/crm/my')
            } else {
                await createIntake(data).unwrap()
                if (rop) {
                    toast.success(formatMessage({ id: 'crmRopRegistered' }, { name: data.studentName }))
                    navigate('/rop/students')
                    return
                }
                setForm(EMPTY)
                setDup('')
                setSaved(formatMessage({ id: 'crmSavedToModerator' }))
            }
        } catch {
            /* общий обработчик ошибок покажет текст */
        }
    }

    const text = (k: keyof Form, opts: { inputMode?: any; placeholder?: string; onBlur?: () => void; digitsOnly?: number } = {}) => (
        <TextField
            size="small"
            fullWidth
            value={form[k] as string}
            error={!!errors[k]}
            inputProps={{ inputMode: opts.inputMode, maxLength: opts.digitsOnly || undefined }}
            placeholder={opts.placeholder}
            onChange={(e) => set(k, (opts.digitsOnly !== undefined ? digits(e.target.value).slice(0, opts.digitsOnly || 12) : e.target.value) as any)}
            onBlur={opts.onBlur}
            autoComplete="off"
        />
    )

    const select = (k: keyof Form, options: { value: string; label: string }[], placeholder: string) => (
        <Select
            size="small"
            fullWidth
            displayEmpty
            value={form[k] as string}
            error={!!errors[k]}
            onChange={(e) => set(k, e.target.value as any)}
            renderValue={(v) => (v ? options.find((o) => o.value === v)?.label : <Box component="span" sx={{ color: ui.muted }}>{placeholder}</Box>)}
        >
            {options.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
        </Select>
    )

    const pick = formatMessage({ id: 'crmChoose' })
    const back = () => navigate(rop ? `/rop/students/${editId}` : '/crm/my')
    const activeManagers = managers.filter((m) => !m.firedAt || m.userId === editing?.sellerId)
    const todayTotal = today.filter((x) => x.status !== 'RETURNED').reduce((s, x) => s + (x.data?.paidSum || 0), 0)
    const sortedToday = useMemo(() => [...today].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)), [today])

    return (
        <Box sx={pageSx}>
            <Box sx={contentSx}>
                {rop && !!editId && (
                    <Box component="button" type="button" onClick={back} sx={backBtn}>
                        ← {formatMessage({ id: 'crmStudentData' })}
                    </Box>
                )}
                <PageHead
                    title={formatMessage({ id: rop && editId ? 'crmRopEditTitle' : 'crmRegisterTitle' })}
                    sub={formatMessage({ id: rop && editId ? 'crmRopEditSub' : 'crmRegisterSub' })}
                />

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: rop ? '1fr' : { xs: '1fr', lg: 'minmax(0, 2fr) minmax(300px, 1fr)' },
                        maxWidth: rop ? 900 : undefined,
                        gap: 2,
                        alignItems: 'start',
                    }}
                >
                    <Card>
                        <CardHead
                            title={editId ? formatMessage({ id: 'crmEditOf' }, { name: form.studentName }) : formatMessage({ id: 'crmNewStudent' })}
                            right={<Pill tone={editId ? 'wait' : 'pink'}>{formatMessage({ id: editId ? 'crmModeEdit' : 'crmModeNew' })}</Pill>}
                        />
                        <form onSubmit={submit} noValidate>
                            {rop && (
                                <Fieldset legend={formatMessage({ id: 'crmWhoseSale' })}>
                                    <F label={formatMessage({ id: 'crmManager' })} required error={errors.sellerId}>
                                        <Select
                                            size="small"
                                            fullWidth
                                            displayEmpty
                                            value={sellerValue}
                                            error={!!errors.sellerId}
                                            onChange={(e) => set('sellerId', e.target.value === '' ? '' : Number(e.target.value))}
                                        >
                                            <MenuItem value="" disabled>{pick}</MenuItem>
                                            <MenuItem value={0}>{formatMessage({ id: 'crmRopSelf' })}</MenuItem>
                                            {activeManagers.map((m) => (
                                                <MenuItem key={m.userId} value={m.userId}>{m.name || '-'}</MenuItem>
                                            ))}
                                        </Select>
                                    </F>
                                    <F>
                                        <Hint sx={{ marginTop: { sm: '26px' } }}>{formatMessage({ id: 'crmWhoseSaleHint' })}</Hint>
                                    </F>
                                </Fieldset>
                            )}
                            <Fieldset legend={formatMessage({ id: 'crmStudentAndParent' })}>
                                <F label={formatMessage({ id: 'crmStudentName' })} required error={errors.studentName}>{text('studentName')}</F>
                                <F label={formatMessage({ id: 'crmStudentPhone' })} required error={errors.studentPhone}>
                                    {text('studentPhone', {
                                        inputMode: 'tel',
                                        placeholder: '+7 7__ ___ __ __',
                                        onBlur: () => {
                                            const v = formatPhone(form.studentPhone)
                                            set('studentPhone', v)
                                            runDuplicate(v, form.iin)
                                        },
                                    })}
                                </F>
                                <F label={formatMessage({ id: 'crmIin' })} required error={errors.iin}>
                                    {text('iin', { inputMode: 'numeric', placeholder: formatMessage({ id: 'crmIinPlaceholder' }), digitsOnly: 12, onBlur: () => runDuplicate(form.studentPhone, form.iin) })}
                                </F>
                                <F label={formatMessage({ id: 'crmGrade' })} required error={errors.grade}>
                                    {select('grade', GRADES.map((g) => ({ value: g, label: dictLabel(formatMessage, 'Grade', g) })), pick)}
                                </F>
                                <F label={formatMessage({ id: 'crmLanguage' })} required error={errors.language}>
                                    {select('language', LANGS.map((g) => ({ value: g, label: dictLabel(formatMessage, 'Lang', g) })), pick)}
                                </F>
                                {dup && (
                                    <Box sx={{ gridColumn: '1 / -1', background: tone.warn.bg, color: tone.warn.text, border: `1px solid ${tone.warn.border}`, borderRadius: '10px', padding: '8px 12px', fontSize: '13px' }}>
                                        {dup}
                                    </Box>
                                )}
                                <F label={formatMessage({ id: 'crmParentName' })} required error={errors.parentName} first>{text('parentName')}</F>
                                <F label={formatMessage({ id: 'crmParentPhone' })} required error={errors.parentPhone}>
                                    {text('parentPhone', { inputMode: 'tel', placeholder: '+7 7__ ___ __ __', onBlur: () => set('parentPhone', formatPhone(form.parentPhone)) })}
                                </F>
                            </Fieldset>

                            <Fieldset legend={formatMessage({ id: 'crmPackage' })}>
                                <F label={formatMessage({ id: 'crmPackageLabel' })} required error={errors.packageCode}>
                                    <Select
                                        size="small"
                                        fullWidth
                                        displayEmpty
                                        value={form.packageCode}
                                        error={!!errors.packageCode}
                                        onChange={(e) => {
                                            const code = e.target.value
                                            const price = dict.find('PACKAGE', code)?.price
                                            setForm((f) => ({ ...f, packageCode: code, paidSum: !f.paidSum.trim() && price ? String(price) : f.paidSum }))
                                        }}
                                        renderValue={(v) => (v ? dict.label('PACKAGE', v) : <Box component="span" sx={{ color: ui.muted }}>{pick}</Box>)}
                                    >
                                        {dict.options('PACKAGE', form.packageCode).map((o) => (
                                            <MenuItem key={o.code} value={o.code || ''}>
                                                {dict.title(o)}
                                                {o.price ? <Box component="span" sx={{ color: ui.muted, marginLeft: '8px' }}>{money(o.price)}</Box> : null}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </F>
                            </Fieldset>

                            <Fieldset legend={formatMessage({ id: 'crmMainSubjects' })}>
                                <F full>
                                    <Chips>
                                        {mainSubjects.map((s) => (
                                            <Chip key={s.id} on={form.subjectIds.includes(s.id)} onClick={() => set('subjectIds', toggle(form.subjectIds, s.id))}>
                                                {subjectTitle(s, locale)}
                                            </Chip>
                                        ))}
                                    </Chips>
                                </F>
                            </Fieldset>

                            <Fieldset legend={formatMessage({ id: 'crmProfileSubjects' })}>
                                <F full error={errors.subjectIds}>
                                    <Chips>
                                        {profileSubjects.map((s) => (
                                            <Chip key={s.id} on={form.subjectIds.includes(s.id)} onClick={() => set('subjectIds', toggle(form.subjectIds, s.id))}>
                                                {subjectTitle(s, locale)}
                                            </Chip>
                                        ))}
                                    </Chips>
                                    <Hint sx={{ marginTop: '6px' }}>{formatMessage({ id: 'crmSubjectsHint' })}</Hint>
                                </F>
                            </Fieldset>

                            <Fieldset legend={formatMessage({ id: 'crmPayment' })}>
                                <F full label={formatMessage({ id: 'crmPayType' })} required hint={formatMessage({ id: 'crmOne' })} error={errors.payType}>
                                    <Chips>
                                        {dict.options('PAY_TYPE', form.payType).map((o) => (
                                            <Chip key={o.code} on={form.payType === o.code} onClick={() => set('payType', form.payType === o.code ? '' : o.code || '')}>
                                                {dict.title(o)}
                                            </Chip>
                                        ))}
                                    </Chips>
                                </F>
                                {installment && (
                                    <Box sx={{ gridColumn: '1 / -1', background: TONES.wait.bg, borderRadius: '12px', padding: '12px', minWidth: 0 }}>
                                        <FieldLabel required>{formatMessage({ id: 'crmNextPaySum' })}</FieldLabel>
                                        <Box sx={{ maxWidth: 320 }}>
                                            {text('nextPaySum', { inputMode: 'numeric', digitsOnly: 0, placeholder: formatMessage({ id: 'crmNextPayPlaceholder' }) })}
                                        </Box>
                                        <Hint sx={{ marginTop: '4px' }}>{formatMessage({ id: 'crmNextPayHint' })}</Hint>
                                        <ErrorText text={errors.nextPaySum} />
                                    </Box>
                                )}
                                <F full label={formatMessage({ id: 'crmPayMethod' })} required hint={formatMessage({ id: 'crmMultiple' })} error={errors.payments}>
                                    <Chips>
                                        {dict.options('PAY_METHOD', form.payments).map((o) => (
                                            <Chip key={o.code} on={form.payments.includes(o.code || '')} onClick={() => set('payments', toggle(form.payments, o.code || ''))}>
                                                {dict.title(o)}
                                            </Chip>
                                        ))}
                                    </Chips>
                                </F>
                                <F label={formatMessage({ id: 'crmPaidSum' })} required error={errors.paidSum}>{text('paidSum', { inputMode: 'numeric', digitsOnly: 0 })}</F>
                                <F label={formatMessage({ id: 'crmDiscountSum' })}>
                                    {text('discountSum', { inputMode: 'numeric', digitsOnly: 0, placeholder: formatMessage({ id: 'crmDiscountPlaceholder' }) })}
                                </F>
                                <F label={formatMessage({ id: 'crmContractUrl' })} error={errors.contractUrl}>{text('contractUrl', { inputMode: 'url', placeholder: 'https://…' })}</F>
                                <F label={formatMessage({ id: 'crmQuota' })} hint={formatMessage({ id: 'crmQuotaHint' })}>
                                    <Select size="small" fullWidth displayEmpty value={form.quota} onChange={(e) => set('quota', e.target.value)}>
                                        <MenuItem value="">{formatMessage({ id: 'crmQuotaNone' })}</MenuItem>
                                        {QUOTAS.map((q) => (
                                            <MenuItem key={q} value={q}>{dictLabel(formatMessage, 'Quota', q)}</MenuItem>
                                        ))}
                                    </Select>
                                </F>
                            </Fieldset>

                            <Fieldset legend={formatMessage({ id: 'crmSourceAndOrder' })}>
                                <F full label={formatMessage({ id: 'crmSource' })} required hint={formatMessage({ id: 'crmMultiple' })} error={errors.sources}>
                                    <Chips>
                                        {dict.options('SOURCE', form.sources).map((o) => (
                                            <Chip key={o.code} on={form.sources.includes(o.code || '')} onClick={() => set('sources', toggle(form.sources, o.code || ''))}>
                                                {dict.title(o)}
                                            </Chip>
                                        ))}
                                    </Chips>
                                </F>
                                <F full label={formatMessage({ id: 'crmAmoUrl' })} required error={errors.amoUrl}>
                                    {text('amoUrl', { inputMode: 'url', placeholder: 'https://dostyq.amocrm.ru/leads/detail/…' })}
                                </F>
                                <F label={formatMessage({ id: 'crmBookOrderNo' })}>{text('bookOrderNo')}</F>
                                <F label={formatMessage({ id: 'crmBookSum' })}>
                                    {text('bookSum', { inputMode: 'numeric', digitsOnly: 0, placeholder: formatMessage({ id: 'crmBookSumPlaceholder' }) })}
                                </F>
                                <F full label={formatMessage({ id: 'crmNote' })}>
                                    <TextField size="small" fullWidth multiline minRows={2} value={form.note} onChange={(e) => set('note', e.target.value)} />
                                </F>
                            </Fieldset>

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', marginTop: '8px' }}>
                                <PrimaryButton type="submit" disabled={creating || updating}>
                                    {formatMessage({ id: editId ? 'crmSaveChanges' : 'crmSave' })}
                                </PrimaryButton>
                                {!!editId && <OutlineButton onClick={back}>{formatMessage({ id: 'cancel' })}</OutlineButton>}
                                {Object.keys(errors).length > 0 && (
                                    <Box sx={{ color: tone.bad.text, fontSize: '13px' }}>{formatMessage({ id: 'crmFixRed' })}</Box>
                                )}
                                {saved && <Box sx={{ color: tone.ok.text, fontSize: '13px', fontWeight: 600 }}>{saved}</Box>}
                            </Box>
                        </form>
                    </Card>

                    {!rop && <Card>
                        <CardHead
                            title={formatMessage({ id: 'crmTodayRegistered' })}
                            right={formatMessage({ id: 'crmCountSum' }, { count: today.length, sum: money(todayTotal) })}
                        />
                        {sortedToday.length === 0 && <Empty text={formatMessage({ id: 'crmTodayEmpty' })} />}
                        {sortedToday.map((x) => {
                            const st = sellerStage(formatMessage, x)
                            return (
                                <Box
                                    key={x.id}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '44px minmax(0, 1fr) auto',
                                        gap: '4px 8px',
                                        alignItems: 'center',
                                        padding: '8px 6px',
                                        borderBottom: `1px solid ${ui.border}`,
                                        borderRadius: '8px',
                                        background: x.id === editId ? ui.pinkSoft : 'transparent',
                                    }}
                                >
                                    <Muted>{hm(x.createdAt)}</Muted>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Box sx={{ fontWeight: 600, fontSize: '13.5px', overflowWrap: 'anywhere' }}>{x.data?.studentName}</Box>
                                        <Muted>
                                            {dict.label('PACKAGE', x.data?.packageCode)} · {money(x.data?.paidSum)}
                                        </Muted>
                                        <Box sx={{ marginTop: '4px' }}>
                                            <Pill tone={st.tone} sx={{ whiteSpace: 'normal' }}>{st.text}</Pill>
                                        </Box>
                                    </Box>
                                    <IconButton size="small" aria-label={formatMessage({ id: 'crmEdit' })} onClick={() => setParams({ edit: String(x.id) })}>
                                        <Box component="span" sx={{ fontSize: '16px', color: ui.pink }}>✎</Box>
                                    </IconButton>
                                </Box>
                            )
                        })}
                    </Card>}
                </Box>
            </Box>
        </Box>
    )
}

const backBtn = { border: 'none', background: 'transparent', color: ui.pink, fontWeight: 600, fontSize: '13px', cursor: 'pointer', padding: '0 0 10px', font: 'inherit' }

const Fieldset: React.FC<{ legend: string; children: React.ReactNode }> = ({ legend, children }) => (
    <Box component="fieldset" sx={{ border: 'none', borderTop: `1px solid ${ui.border}`, margin: '0 0 8px', padding: '12px 0 4px', minWidth: 0 }}>
        <Box component="legend" sx={{ fontSize: '12px', fontWeight: 700, color: ui.muted, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '0 8px 0 0' }}>
            {legend}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '12px 14px' }}>{children}</Box>
    </Box>
)

const F: React.FC<{ label?: string; required?: boolean; hint?: string; error?: string; full?: boolean; first?: boolean; children: React.ReactNode }> = ({
    label,
    required,
    hint,
    error,
    full,
    first,
    children,
}) => (
    <Box sx={{ gridColumn: full ? '1 / -1' : first ? { sm: '1' } : undefined, minWidth: 0 }}>
        {label && <FieldLabel required={required} hint={hint}>{label}</FieldLabel>}
        {children}
        <ErrorText text={error} />
    </Box>
)

export default RegisterPage
